import React, { useState, useEffect } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import { format, set } from 'date-fns';
import { getData, deleteData } from '../utils/helper';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { PropagateLoader } from "react-spinners";

const CSSProperties = {
    display: "block",
    margin: "0 auto",
    borderColor: "red",
};

const Employees = () => {
    const iconColor = '#00BFFF';
    let [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);

    const handleAddNew = () => {
        navigate('/employees-form');
    };

    const handleEdit = (id) => {
        const rowToEdit = rows.find(row => row.id === id);
        navigate('/employees-form', { state: { editData: rowToEdit } });
    };

    const handleDelete = async (data) => {
        setLoading(true);
        const response = await deleteData('employees/' + data.id);
        setLoading(false);
        if (response.status === 401) {
            navigate('/', { state: { message: "Session Expired" } });
            setLoading(false);
            return;
        }
        if (response.status === undefined) {
            setRows(rows.filter(item => item.id !== data.id));
            toast.success('Item deleted successfully', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        } else {
            toast.errpr('Error while deletion', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        }
    };

    const columns = [
        // { field: 'id', headerName: 'ID', width: 60 },
        { field: 'code', headerName: 'Code', width: 70 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'email', headerName: 'Email', width: 200 },
        { field: 'phone', headerName: 'Phone', width: 120 },
        { field: 'appointment_date', headerName: 'Appointment Date', width: 140 },
        { field: 'bank_name', headerName: 'Bank Name', width: 150 },
        { field: 'account_title', headerName: 'Account Title', width: 150 },
        { field: 'iban', headerName: 'IBAN', width: 200 },
        { field: 'cnic', headerName: 'CNIC', width: 150 },
        { field: 'position', headerName: 'Position', width: 150 },
        { field: 'grade', headerName: 'Grade', width: 120 },
        {
            field: 'basic_salary',
            headerName: 'Basic Salary',
            width: 120,
            renderCell: (params) => {
                if (params.value == null) return '-';

                // Format with commas and keep decimal places
                const formattedValue = new Intl.NumberFormat('en-IN', {
                    minimumFractionDigits: 0,  // Ensures 2 decimal places (e.g., 50000 → 50,000.00)
                    maximumFractionDigits: 0,  // Prevents extra decimals (e.g., 50000.555 → 50,000.56)
                }).format(params.value);

                return `Rs. ${formattedValue}`;
            }
        },
        {
            field: 'updated_at',
            headerName: 'Date Time',
            width: 150,
            renderCell: (params) => {
                if (!params.value) return '';

                // Format the date manually
                const formatDate = (dateString) => {
                    const date = new Date(dateString);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    const seconds = String(date.getSeconds()).padStart(2, '0');

                    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                };

                return (
                    <div style={{ width: '100%' }}>
                        {formatDate(params.value)}
                    </div>
                );
            }
        },
        {
            field: 'edit',
            headerName: 'Edit',
            width: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title="Edit">
                    <IconButton
                        onClick={() => handleEdit(params.row.id)}
                        size="small"
                        sx={{ color: iconColor }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
        {
            field: 'delete',
            headerName: 'Delete',
            width: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title="Edit">
                    <IconButton
                        onClick={() => handleDelete(params.row)}
                        size="small"
                        sx={{ color: iconColor }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            try {
                const data = await getData('employees');
                if (data.status === 401) {
                    navigate('/', { state: { message: "Session Expired" } });
                    setLoading(false);
                    return;
                }
                setRows(data?.data);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                toast.error('Error Fetching Data', {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                });
            }
        };
        fetchData();
    }, []);

    return (
        <>
            {loading && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', // Optional: semi-transparent white background
                    zIndex: 9999, // Ensure it's above other elements
                }}>
                    <PropagateLoader
                        color="#00BFFF"
                        loading={loading}
                        cssOverride={CSSProperties}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </div>
            )}
            <Paper elevation={3} sx={{ p: 2, height: '800px' }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                }}>
                    <Typography variant="h6" component="h2">
                        Employee List
                    </Typography>
                    <ToastContainer />
                    <Tooltip title="Add New Expense">
                        <IconButton
                            aria-label="add new"
                            onClick={handleAddNew}
                            size="large"
                            sx={{ color: iconColor }}
                        >
                            <AddCircleIcon fontSize="large" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <div style={{ height: 500, width: '100%' }}>
                    <DataGridPro
                        rows={rows}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5, 10, 20]}
                    />
                </div>
            </Paper>
        </>
    );
};

export default Employees;